import './Live.sass';

import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { liveActions, liveSelector } from '$store/live';
import { LiveProps } from '$core/Live/Live.interfca';
import { LiveStatus, LiveType } from '$store/live/interface';
import { Loader } from '$shared/components';
import { LivePublisher } from '$core/Live/LivePublisher/LivePublisher';
import { LiveViewer } from '$core/Live/LiveViewer/LiveViewer';
import { authSelector } from '$store/auth';
import { LiveUserActionsSheet } from '$core/Live/LiveUserActionsSheet/LiveUserActionsSheet';
import { removeTargetModal, setStorageItem, setTargetModal } from '$utils';
import { ContentPriorityType } from '$store/main/interface';
import { useProfileInfo, useTranslator } from '$hooks';
import { currentProfileSelector } from '$store/profile';
import { goBack, showModal } from '$navigation/helper';
import { toastActions } from '$store/toast';
import { LiveTotalTipsSheet } from '$core/Live/LiveTotalTipsSheet/LiveTotalTipsSheet';
import { LiveOnlineUsersSheet } from '$core/Live/LiveOnlineUsersSheet/LiveOnlineUsersSheet';

export const Live: FC<LiveProps> = ({ liveId, profile }) => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const { isLoading, isLogged } = useSelector(authSelector);
  const video = useRef<HTMLVideoElement>(null);
  const { mediaStream, liveStatus, type, cameras, currentCameraId } =
    useSelector(liveSelector);

  const profileInfo = useSelector(currentProfileSelector);

  useEffect(() => {
    if (profile && profileInfo) {
      if (profileInfo.stream?.id) {
        showModal('live' + profileInfo.stream?.id, true, {}, { click: false });
      } else {
        dispatch(toastActions.setToastFail(t('live_finished_title')));
        goBack();
      }
    }
  }, [profileInfo, profile]);

  useEffect(() => {
    if (mediaStream && video.current && !profile) {
      video.current.srcObject = mediaStream;
      video.current.play().catch((e) => {
        console.error(e);
      });
    }
  }, [video, mediaStream, profile]);

  useEffect(() => {
    if (!isLoading && liveId) {
      isLogged ? removeTargetModal() : setTargetModal('live' + liveId);
    }
    if (isLoading || profile) return;
    if (liveId) {
      dispatch(liveActions.startWatchLive(liveId));
    } else {
      dispatch(liveActions.initLive());
    }
  }, [liveId, isLoading, profile, isLogged]);

  useEffect(
    () => () => {
      if (!profile) {
        dispatch(liveActions.stopStream());
        dispatch(liveActions.clear());
      }
    },
    [],
  );

  useEffect(() => {
    setStorageItem('contentPriorityType', ContentPriorityType.ADULT);
  }, []);

  return (
    <div className="Live">
      {liveStatus === LiveStatus.INIT ? (
        <Loader isWrapped />
      ) : type === LiveType.PUBLISHER ? (
        <LivePublisher />
      ) : (
        <LiveViewer />
      )}
      <LiveUserActionsSheet />
      <LiveTotalTipsSheet />
      <LiveOnlineUsersSheet />
    </div>
  );
};
